import * as DialogPrimitive from '@radix-ui/react-dialog'
import type React from 'react'
import { cn } from '../lib/utils'

const sizes = {
    xs: 'sm:max-w-xs',
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
}

export function Dialog({
    size = 'lg',
    className,
    children,
    open,
    onClose,
    ...props
}: {
    size?: keyof typeof sizes
    className?: string
    children: React.ReactNode
    open: boolean
    onClose: (value: boolean) => void
} & Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>, 'open' | 'onOpenChange' | 'children'>) {
    return (
        <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onClose(false)} {...props}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 transition duration-100 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 sm:px-6 sm:py-8 lg:px-8 lg:py-16 dark:bg-zinc-950/50" />

                <div className="fixed inset-0 z-50 w-screen overflow-y-auto pt-6 sm:pt-0">
                    <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
                        <DialogPrimitive.Content
                            className={cn(
                                sizes[size],
                                'row-start-2 flex max-h-[85vh] w-full min-w-0 flex-col overflow-hidden rounded-t-[2rem] bg-white p-[--gutter] shadow-lg ring-1 ring-zinc-950/10 [--gutter:theme(spacing.8)] sm:mb-auto sm:rounded-3xl dark:bg-zinc-900 dark:ring-white/10 forced-colors:outline',
                                'duration-100 will-change-transform data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom-12 data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-12 data-[state=open]:fade-in-0 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95',
                                className
                            )}
                        >
                            {children}
                        </DialogPrimitive.Content>
                    </div>
                </div>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    )
}

export function DialogTitle({
    className,
    ...props
}: { className?: string } & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            {...props}
            className={cn('shrink-0 text-balance text-lg/6 font-semibold text-zinc-950 sm:text-base/6 dark:text-white', className)}
        />
    )
}

export function DialogDescription({
    className,
    ...props
}: { className?: string } & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) {
    return <DialogPrimitive.Description {...props} className={cn('mt-2 shrink-0 text-pretty text-base/6 text-muted-foreground sm:text-sm/6', className)} />
}

export function DialogBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
    return <div {...props} className={cn('mt-6 min-h-0 flex-1 overflow-y-auto', className)} />
}

export function DialogActions({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <div
            {...props}
            className={cn(
                'mt-8 flex shrink-0 flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto',
                className
            )}
        />
    )
}
